import { useAnalytics } from '@listening/frontend/analytics/client';
import { clientEnv } from '@/env';
import { ClientOnly } from '@tanstack/react-router';
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  type Placement,
} from '@floating-ui/react-dom';
import { NavArrowLeft, NavArrowRight, Xmark } from 'iconoir-react';
import { useEffect, useRef, useState, type CSSProperties } from 'react';

const storageKey = 'listening.publicDocTutorial.v1.completed';

type TourStep = {
  body: string;
  // Ordered preference: first visible match wins. `more-menu` is a fallback
  // for mobile where the desktop-only target isn't rendered (download button,
  // pdf/text switcher) — both live inside the kebab dropdown there.
  targets: ReadonlyArray<string>;
  placement: Placement;
};

const steps: ReadonlyArray<TourStep> = [
  {
    body: 'You can download the PDF here',
    targets: ['download', 'more-menu'],
    placement: 'bottom',
  },
  {
    body: 'You can read the PDF here',
    targets: ['view-switcher', 'more-menu'],
    placement: 'bottom',
  },
  {
    body: 'You can play the audio of this PDF here',
    targets: ['play'],
    placement: 'top',
  },
];

// Arrow is drawn as an actual triangle via clip-path, not a rotated square,
// so the half-buried-in-tooltip trick (which depends on arrow and tooltip body
// rendering at identical color values) isn't needed.
const ARROW_WIDTH = 28;
const ARROW_HEIGHT = 14;

// Module-scope guard so `shown` fires exactly once per page load, surviving
// React 18 StrictMode double-mount in dev.
let shownEventFired = false;

// Dev/QA helper: re-trigger the tutorial after dismissal. Non-production
// only — the if-guard runs at module load (no-op in SSR + prod bundles).
if (
  typeof window !== 'undefined' &&
  clientEnv.mode !== 'production' &&
  !('resetPublicDocTutorial' in window)
) {
  Object.defineProperty(window, 'resetPublicDocTutorial', {
    value: () => {
      localStorage.removeItem(storageKey);
      window.location.reload();
    },
    writable: false,
    configurable: true,
  });
}

export function PublicDocTutorial() {
  // ClientOnly must render unconditionally so the server tree and the
  // first-paint client tree have the same fragment shape.
  return (
    <ClientOnly>
      <PublicDocTutorialTour />
    </ClientOnly>
  );
}

function PublicDocTutorialTour() {
  const { capture } = useAnalytics();
  const [stepIndex, setStepIndex] = useState(() =>
    localStorage.getItem(storageKey) === 'true' ? null : 0,
  );

  useEffect(() => {
    if (stepIndex === null) return;
    if (shownEventFired) return;
    shownEventFired = true;
    capture('public_doc_tutorial_interaction', {
      interaction: 'shown',
    });
  }, [stepIndex, capture]);

  if (stepIndex === null) return null;

  const step = steps[stepIndex] ?? steps[0];

  const finish = (interaction: 'dismissed' | 'completed') => {
    localStorage.setItem(storageKey, 'true');
    setStepIndex(null);
    capture('public_doc_tutorial_interaction', {
      interaction,
    });
  };

  return (
    <TourStepBubble
      // Force a fresh useFloating instance per step so target resolution and
      // placement preference don't bleed across steps.
      key={stepIndex}
      step={step}
      stepNumber={stepIndex + 1}
      stepCount={steps.length}
      isFirstStep={stepIndex === 0}
      isLastStep={stepIndex === steps.length - 1}
      onPrev={() => setStepIndex(stepIndex - 1)}
      onNext={() => setStepIndex(stepIndex + 1)}
      onFinish={finish}
    />
  );
}

// Resolves the first visible `[data-tour-id=...]` from `candidates`. Re-runs on
// resize and on a low-rate poll so that targets which appear/disappear with the
// responsive layout (scroll-hidden top bar, mobile-only kebab fallback) are
// picked up without wiring a MutationObserver to the whole document.
//
// `width > 0 && height > 0` (not just `getClientRects().length`) so a 0-height
// ancestor like the mobile scroll-hidden top bar (`h-0 overflow-hidden`) still
// counts as not-visible and falls through to the next candidate.
function useTourTarget(candidates: ReadonlyArray<string>) {
  const [el, setEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const resolve = () => {
      for (const id of candidates) {
        const node = document.querySelector<HTMLElement>(
          `[data-tour-id="${id}"]`,
        );
        if (!node) continue;
        const rect = node.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setEl((prev) => (prev === node ? prev : node));
          return;
        }
      }
      setEl((prev) => (prev === null ? prev : null));
    };

    resolve();
    window.addEventListener('resize', resolve);
    const interval = window.setInterval(resolve, 500);
    return () => {
      window.removeEventListener('resize', resolve);
      window.clearInterval(interval);
    };
  }, [candidates]);

  return el;
}

const oppositeSide = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
} as const;

// Triangle pointing away from the tooltip (toward the reference). Keyed by the
// arrow's anchor side — i.e. which tooltip edge the arrow's base sits on. The
// arrow lives entirely outside the tooltip body, so the triangle's apex must
// point away from that anchor edge.
const clipPathByAnchorSide = {
  top: 'polygon(50% 0, 100% 100%, 0 100%)', // arrow above tooltip, points up
  bottom: 'polygon(0 0, 100% 0, 50% 100%)', // arrow below tooltip, points down
  left: 'polygon(100% 0, 100% 100%, 0 50%)', // arrow left of tooltip, points left
  right: 'polygon(0 0, 0 100%, 100% 50%)', // arrow right of tooltip, points right
} as const;

function TourStepBubble({
  step,
  stepNumber,
  stepCount,
  isFirstStep,
  isLastStep,
  onPrev,
  onNext,
  onFinish,
}: {
  step: TourStep;
  stepNumber: number;
  stepCount: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  onPrev: () => void;
  onNext: () => void;
  onFinish: (interaction: 'dismissed' | 'completed') => void;
}) {
  const target = useTourTarget(step.targets);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  const { refs, floatingStyles, middlewareData, placement } = useFloating({
    placement: step.placement,
    strategy: 'fixed',
    middleware: [
      offset(ARROW_HEIGHT),
      flip({ padding: 16 }),
      shift({ padding: 16 }),
      arrow({ element: arrowRef, padding: 16 }),
    ],
    whileElementsMounted: autoUpdate,
    elements: { reference: target ?? undefined },
  });

  // Floating UI may flip the requested side to keep the bubble on-screen; read
  // the arrow's anchor side from the *resolved* placement so the pointer ends
  // up on the correct edge after a flip.
  const resolvedSide = placement.split('-')[0] as keyof typeof oppositeSide;
  const arrowAnchorSide = oppositeSide[resolvedSide];

  // Arrow is drawn as a clipped triangle pointing toward the reference. Width
  // runs along the tooltip edge; height is the protrusion away from it.
  const isVertical = resolvedSide === 'top' || resolvedSide === 'bottom';
  const arrowWidth = isVertical ? ARROW_WIDTH : ARROW_HEIGHT;
  const arrowHeight = isVertical ? ARROW_HEIGHT : ARROW_WIDTH;

  // Floating UI's arrow middleware only populates the axis perpendicular to the
  // placement edge (x for top/bottom, y for left/right); the other axis is
  // pinned to the tooltip edge via `[arrowAnchorSide]`.
  const arrowStyle: CSSProperties = {
    position: 'absolute',
    width: arrowWidth,
    height: arrowHeight,
    background: '#073a55',
    clipPath: clipPathByAnchorSide[arrowAnchorSide],
    ...(middlewareData.arrow?.x !== undefined && {
      left: middlewareData.arrow.x,
    }),
    ...(middlewareData.arrow?.y !== undefined && {
      top: middlewareData.arrow.y,
    }),
    // Pin the arrow flush against the tooltip edge: offset by its protrusion
    // distance (always the small dimension regardless of orientation).
    [arrowAnchorSide]: -ARROW_HEIGHT,
  };

  // Center the bubble in the viewport when no target is visible, so the user
  // can always read & dismiss the step even if the anchor element is hidden.
  const fallbackStyle: CSSProperties = {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  };

  const showArrow = target !== null && middlewareData.arrow !== undefined;

  return (
    <div
      ref={refs.setFloating}
      style={target ? floatingStyles : fallbackStyle}
      className='pointer-events-auto z-[100] w-[min(680px,calc(100vw-2rem))] rounded-2xl bg-[#073a55] px-4 py-3 text-white shadow-[0_18px_60px_rgba(7,58,85,0.35)] sm:px-6 sm:py-4'
      role='dialog'
      aria-live='polite'
      aria-label='Document tutorial'
    >
      <div className='pointer-events-none absolute inset-x-12 top-1/2 h-12 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(255,214,123,0.45),transparent_70%)] blur-xl' />
      {showArrow && <div ref={arrowRef} style={arrowStyle} />}
      <div className='relative flex items-center gap-4'>
        <p className='min-w-0 flex-1 text-base leading-snug font-semibold sm:text-xl'>
          {step.body}
        </p>
        <span className='text-sm font-semibold whitespace-nowrap text-white/70 sm:text-base'>
          {stepNumber} / {stepCount}
        </span>
        {!isFirstStep && (
          <button
            type='button'
            className='flex size-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition hover:bg-white/20'
            onClick={onPrev}
            aria-label='Previous tutorial step'
          >
            <NavArrowLeft className='size-5' />
          </button>
        )}
        {isLastStep ? (
          <button
            type='button'
            className='shrink-0 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#073a55] transition hover:bg-white/90'
            onClick={() => onFinish('completed')}
          >
            OK
          </button>
        ) : (
          <button
            type='button'
            className='flex size-10 shrink-0 items-center justify-center rounded-full border border-white/30 bg-white/10 text-white transition hover:bg-white/20'
            onClick={onNext}
            aria-label='Next tutorial step'
          >
            <NavArrowRight className='size-5' />
          </button>
        )}
        <button
          type='button'
          className='flex size-8 shrink-0 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white'
          onClick={() => onFinish('dismissed')}
          aria-label='Dismiss tutorial'
        >
          <Xmark className='size-4' />
        </button>
      </div>
    </div>
  );
}
